import React, {useCallback, useEffect, useState} from 'react';
import {Navigate} from 'react-router-dom';
import {inject, observer} from 'mobx-react';
import I18n from 'app/i18n';
import {TableFilter} from 'app/components/table/filter';
import {useQuickBetsFilterProps, useQuickBetsFilteredLineIds} from 'app/components/pages/play_content/useQuickBetsData';
import QuickBetsItem from 'app/components/pages/home_page/PreviewGames/QuickBetsItem';
import {ACTIVE_STATUSES} from 'admin/components/bet_form/utils/LineConsts';
import {LazyLoading} from 'app/utils/lazyLoading/LazyLoading';
import {getTableLinkPath} from 'app/utils/link_helper';
import {isAdmin} from 'app/utils/IsAdmin';
import {TableStore} from 'app/store/TableStore';
import {QuickBetStore} from 'app/store/QuickBetStore';
import {QuickBetLineModel} from 'app/store/QuickBetLineModel';
import {BetSlipStore} from 'app/store/BetSlipStore';

const INITIAL_PAGE_SIZE = 9;
const LOAD_MORE_SIZE = 9;

interface QuickBetsContentProps {
    tableStore?: TableStore;
    quickBetStore?: QuickBetStore;
    betSlipStore?: BetSlipStore;
}

interface QuickBetRowProps {
    lineId: number;
    quickBetStore: QuickBetStore;
    gamerActiveCoef: {on: number} | null;
}

const QuickBetRow = observer(({lineId, quickBetStore, gamerActiveCoef}: QuickBetRowProps): React.ReactElement | null => {
    const model: QuickBetLineModel | undefined = quickBetStore.getLine(lineId);

    if (!model) {
        return null;
    }

    return <QuickBetsItem
        model={model}
        closed={!ACTIVE_STATUSES.includes(model.status)}
        gamerActiveCoef={gamerActiveCoef}
    />;
});

interface QuickBetsTableFilterProps {
    quickBetStore: QuickBetStore;
    tableStore: TableStore;
}

const QuickBetsTableFilter = observer((props: QuickBetsTableFilterProps): React.ReactElement => {
    const {quickBetStore, tableStore} = props;

    return <TableFilter {...useQuickBetsFilterProps(quickBetStore, tableStore)} />;
});

const QuickBetsContent = (props: QuickBetsContentProps): React.JSX.Element => {
    const {tableStore, quickBetStore, betSlipStore} = props;
    const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);

    const {filteredLineIds, currentEvents} = useQuickBetsFilteredLineIds(quickBetStore!);

    const visibleIds = filteredLineIds.slice(0, visibleCount);
    const hasMore = visibleCount < filteredLineIds.length;

    const loadMore = useCallback(() => {
        setVisibleCount(prev => Math.min(prev + LOAD_MORE_SIZE, filteredLineIds.length));
    }, [filteredLineIds.length]);

    useEffect(() => {
        setVisibleCount(INITIAL_PAGE_SIZE);
    }, [quickBetStore?.filterStore?.discipline, currentEvents]);

    if (!isAdmin()) {
        return <Navigate to={getTableLinkPath('/play/bets')} replace />;
    }

    return (
        <>
            <QuickBetsTableFilter quickBetStore={quickBetStore!} tableStore={tableStore!} />
            <div className="quick-bets-content">
                {visibleIds.length === 0
                    ? <div className="quick-bets-content__empty">{I18n.t('no_quick_bets')}</div>
                    : <>
                        {visibleIds.map(id => <QuickBetRow
                            key={id}
                            lineId={id}
                            quickBetStore={quickBetStore!}
                            gamerActiveCoef={betSlipStore?.activeLine(id)}
                        />)}
                        <LazyLoading show={hasMore} callback={loadMore} />
                    </>
                }
            </div>
        </>
    );
};

export default inject('tableStore', 'quickBetStore', 'betSlipStore')(observer(QuickBetsContent));
